import { io } from "socket.io-client";

let socket = null;
let reconnectAttempts = 0;
const MAX_RECONNECT_ATTEMPTS = 5;

/**
 * Initialize Socket.IO connection
 * @param {string} backendUrl - Backend URL
 * @param {string} token - JWT token for authentication
 * @returns {object} Socket instance
 */
export function initSocket(backendUrl, token) {
  if (socket && socket.connected) {
    return socket;
  }

  socket = io(backendUrl, {
    auth: {
      token: token,
    },
    transports: ["websocket", "polling"],
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
    reconnectionAttempts: MAX_RECONNECT_ATTEMPTS,
  });

  // Connection handlers
  socket.on("connect", () => {
    reconnectAttempts = 0;
  });

  socket.on("connect_error", (error) => {
    console.error("❌ Socket connection error:", error.message);
    reconnectAttempts++;
    if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      console.error("❌ Max reconnection attempts reached");
    }
  });

  socket.on("error", (error) => {
    console.error("❌ Socket error:", error);
  });

  return socket;
}

/**
 * Get socket instance
 * @returns {object} Socket instance
 */
export function getSocket() {
  return socket;
}

/**
 * Disconnect socket
 */
export function disconnectSocket() {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
}

/**
 * Check if socket is connected
 * @returns {boolean}
 */
export function isSocketConnected() {
  return socket && socket.connected;
}

/**
 * Listen to event
 * @param {string} event - Event name
 * @param {function} callback - Callback function
 */
export function on(event, callback) {
  if (!socket) {
    return;
  }
  socket.on(event, callback);
}

/**
 * Remove event listener
 * @param {string} event - Event name
 * @param {function} callback - Callback function
 */
export function off(event, callback) {
  if (!socket) {
    return;
  }
  socket.off(event, callback);
}

/**
 * Emit event
 * @param {string} event - Event name
 * @param {object} data - Data to emit
 */
export function emit(event, data) {
  if (!socket) {
    return;
  }
  socket.emit(event, data);
}

/**
 * Wait for event response (promise-based)
 * @param {string} event - Event name
 * @param {object} data - Data to emit
 * @param {number} timeout - Timeout in milliseconds
 * @returns {Promise} Promise that resolves with response
 */
export function request(event, data, timeout = 5000) {
  return new Promise((resolve, reject) => {
    if (!socket) {
      reject(new Error("Socket not initialized"));
      return;
    }

    const timeoutId = setTimeout(() => {
      reject(new Error(`Event ${event} timed out`));
    }, timeout);

    socket.emit(event, data, (response) => {
      clearTimeout(timeoutId);
      resolve(response);
    });
  });
}
